import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'

// The profile photo (handoff §7b 672, 752-754): "The list still falls back to
// initials where no photo exists." The photo is fetched as JSON (base64 and
// its type) because the API authenticates with a bearer token an <img src>
// cannot carry; it is shown as a data: URL, which a browser never runs as a
// page. One fetch per person and upload, cached for the session, and only
// once the avatar scrolls into view where the browser can tell.

const cache = new Map<string, Promise<string | null>>()

function photoUrl(user: Parameters<typeof apiJson>[0], id: string, at: string): Promise<string | null> {
  const key = `${id}@${at}`
  let p = cache.get(key)
  if (!p) {
    p = apiJson<{ content_type: string; data: string }>(user, `/api/personnel/${id}/photo`)
      .then((r) => (r?.data ? `data:${r.content_type};base64,${r.data}` : null))
      .catch(() => {
        cache.delete(key)
        return null
      })
    cache.set(key, p)
  }
  return p
}

interface Props {
  id: string
  photoAt: string | null | undefined
  initials: string
  className: string
}

export function PersonAvatar({ id, photoAt, initials, className }: Props) {
  const { user } = useAuth()
  const ref = useRef<HTMLSpanElement>(null)
  const [src, setSrc] = useState<string | null>(null)
  const [seen, setSeen] = useState(typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    if (seen || !photoAt || !ref.current) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setSeen(true)
        io.disconnect()
      }
    })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [photoAt, seen])

  useEffect(() => {
    setSrc(null)
    if (!photoAt || !seen) return
    let live = true
    photoUrl(user, id, photoAt).then((u) => live && setSrc(u))
    return () => {
      live = false
    }
  }, [id, photoAt, seen])

  return (
    <span ref={ref} className={className} aria-hidden="true" style={src ? { overflow: 'hidden', padding: 0 } : undefined}>
      {src ? <img src={src} alt="" data-testid="avatar-photo" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : initials}
    </span>
  )
}

export const PHOTO_MAX = 262144
const PHOTO_EDGE = 512

// Shrinks a chosen image to at most 512 px on its longer side as a JPEG, so a
// phone photo fits the 256 KB the server keeps. Where the browser cannot
// decode it here, the file goes as chosen and the server's own checks decide.
export async function shrinkPhoto(file: Blob): Promise<Blob> {
  try {
    if (typeof createImageBitmap !== 'function') return file
    const bmp = await createImageBitmap(file)
    const scale = Math.min(1, PHOTO_EDGE / Math.max(bmp.width, bmp.height))
    if (scale === 1 && file.size <= PHOTO_MAX) return file
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bmp.width * scale)
    canvas.height = Math.round(bmp.height * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height)
    const out = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.85))
    return out || file
  } catch {
    return file
  }
}

export function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''))
    r.onerror = () => reject(r.error)
    r.readAsDataURL(blob)
  })
}
